// skipcq: JS-W1028, JS-0128
/* eslint-disable react-hooks/set-state-in-effect -- dialog resets form when open, mode, or editBeer changes */
import React, { useState, useEffect, useCallback, useMemo, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import { useAuthState } from 'react-firebase-hooks/auth';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Autocomplete from '@mui/material/Autocomplete';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import BeerStyleTypeFields from './BeerStyleTypeFields';
import { DuplicateAddBeerResults } from './DuplicateBeerComparison';
import BeerLocationAvailabilitySelector from './BeerLocationAvailabilitySelector';
import { auth } from '../../utils/firebase';
import { useBeerAddBeer } from '../../hooks/useBeerAddBeer';
import { useBeerAvailability } from '../../hooks/useBeerAvailability';
import { useAddBeerLocationOptions } from '../../hooks/useAddBeerLocationOptions';
import { BrewerySessionContext, defaultBrewerySession } from '../../context/BrewerySessionContext';
import { BrewhouseBeerCatalogContext } from '../../context/BrewhouseBeerCatalogContext';
import {
  trackAddBeerActiveLocationUnchecked,
  trackAddBeerCreateAnyway,
  trackAddBeerOfferReuse,
  trackAddBeerRecoveredFromError,
  trackAddBeerReuseExisting,
  trackAddBeerSelectLocations,
  trackAddBeerTransientUnconfirmed,
} from '../../utils/brewhousePortalAnalytics';
import {
  ADD_BEER_LOADING_LABEL,
  ADD_BEER_RECONCILE_LABEL,
  getUserFacingCallableError,
} from '../../utils/callableErrorMessages';
import { useBeerUpdate } from '../../hooks/useBeerUpdate';
import { useBreweryAutocompleteSearch } from '../../hooks/useBreweryAutocompleteSearch';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import useBeerStyles from '../../hooks/useBeerStyles';
import {
  applyNormalizedAddResponse,
  reconcileAddBeerAfterCallableError,
  buildSavePayload,
  canForceAddDuplicate,
  getDialogTitle,
  getDuplicateReviewId,
  getEditStyleHydrationFields,
  getMatchedBeerForReuse,
  getInitialLocationIds,
  getOpenDialogFormReset,
  haveLocationIdsChanged,
  isGroupDuplicateOffer,
  parseAbvFromForm,
  resolveStyleForSave,
  validateAddBeerForm,
} from './addBeerDialogHelpers';

function AddBeerDialogAlerts({
  errorMessage,
  formErrors,
  serverFieldErrors,
  warningMessage,
  statusMessage,
}) {
  return (
    <>
      {statusMessage ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {statusMessage}
        </Alert>
      ) : null}
      {warningMessage ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {warningMessage}
        </Alert>
      ) : null}
      {(errorMessage || formErrors.general) && !warningMessage ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {formErrors.general || errorMessage}
        </Alert>
      ) : null}
      {serverFieldErrors && serverFieldErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
            Could not add beer
          </Typography>
          <Box component="ul" sx={{ pl: 2, mb: 0 }}>
            {serverFieldErrors.map((errEntry) => (
              <li
                key={`server-err-${errEntry.id}-${errEntry.name}-${errEntry.message}`}
              >
                <Typography variant="body2">{errEntry.message}</Typography>
              </li>
            ))}
          </Box>
        </Alert>
      )}
    </>
  );
}

AddBeerDialogAlerts.propTypes = {
  errorMessage: PropTypes.string,
  formErrors: PropTypes.object.isRequired,
  serverFieldErrors: PropTypes.arrayOf(PropTypes.object),
  warningMessage: PropTypes.string,
  statusMessage: PropTypes.string,
};

AddBeerDialogAlerts.defaultProps = {
  errorMessage: '',
  serverFieldErrors: null,
  warningMessage: '',
  statusMessage: '',
};

function AddBeerDialogFormFields({
  breweryLocked,
  isEditMode,
  breweryOptions,
  brewerySearchLoading,
  selectedBrewery,
  breweryInput,
  formData,
  formErrors,
  styleType,
  catalogStyleId,
  styleLabel,
  loading,
  onBreweryAutocompleteChange,
  onBreweryInputChange,
  getBreweryOptionLabel,
  isBreweryOptionEqualToValue,
  renderBreweryInput,
  onNameChange,
  onStyleTypeChange,
  onStyleSelectionChange,
  onAbvChange,
  onDescriptionChange,
}) {
  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      {!breweryLocked && !isEditMode ? (
        <Autocomplete
          options={breweryOptions}
          loading={brewerySearchLoading}
          value={selectedBrewery}
          onChange={onBreweryAutocompleteChange}
          inputValue={breweryInput}
          onInputChange={onBreweryInputChange}
          getOptionLabel={getBreweryOptionLabel}
          isOptionEqualToValue={isBreweryOptionEqualToValue}
          renderInput={renderBreweryInput}
        />
      ) : null}
      <TextField
        label="Beer name"
        value={formData.name}
        onChange={onNameChange}
        required
        fullWidth
        error={formErrors.name !== undefined}
        helperText={formErrors.name}
      />
      <BeerStyleTypeFields
        styleType={styleType}
        onStyleTypeChange={onStyleTypeChange}
        catalogStyleId={catalogStyleId}
        styleLabel={styleLabel}
        onStyleSelectionChange={onStyleSelectionChange}
        required
        styleTypeError={formErrors.styleType}
        styleError={formErrors.style}
        disabled={loading}
      />
      <TextField
        label="ABV"
        type="text"
        value={formData.abv}
        onChange={onAbvChange}
        required
        fullWidth
        error={formErrors.abv !== undefined}
        helperText={formErrors.abv || '0 or greater, max one decimal place'}
        inputProps={{
          inputMode: 'decimal',
          pattern: '[0-9]*\\.?[0-9]?',
        }}
      />
      <TextField
        label="Description"
        value={formData.description}
        onChange={onDescriptionChange}
        fullWidth
        multiline
        minRows={3}
        helperText="Optional"
      />
    </Stack>
  );
}

AddBeerDialogFormFields.propTypes = {
  breweryLocked: PropTypes.bool.isRequired,
  isEditMode: PropTypes.bool.isRequired,
  breweryOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  brewerySearchLoading: PropTypes.bool.isRequired,
  selectedBrewery: PropTypes.object,
  breweryInput: PropTypes.string.isRequired,
  formData: PropTypes.shape({
    name: PropTypes.string,
    abv: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
  formErrors: PropTypes.object.isRequired,
  styleType: PropTypes.string.isRequired,
  catalogStyleId: PropTypes.string.isRequired,
  styleLabel: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  onBreweryAutocompleteChange: PropTypes.func.isRequired,
  onBreweryInputChange: PropTypes.func.isRequired,
  getBreweryOptionLabel: PropTypes.func.isRequired,
  isBreweryOptionEqualToValue: PropTypes.func.isRequired,
  renderBreweryInput: PropTypes.func.isRequired,
  onNameChange: PropTypes.func.isRequired,
  onStyleTypeChange: PropTypes.func.isRequired,
  onStyleSelectionChange: PropTypes.func.isRequired,
  onAbvChange: PropTypes.func.isRequired,
  onDescriptionChange: PropTypes.func.isRequired,
};

AddBeerDialogFormFields.defaultProps = {
  selectedBrewery: null,
};

function AddBeerDialogActionButtons({
  showDuplicateActions,
  showReuseAction,
  showAddAnywayAction,
  loading,
  loadingLabel,
  reuseLoading,
  addAnywayLoading,
  isEditMode,
  onClose,
  onBackFromDuplicates,
  onUseExistingBeer,
  onAddAnyway,
  onSubmit,
}) {
  if (showDuplicateActions) {
    return (
      <>
        <Button onClick={onClose} disabled={loading}>
          Close
        </Button>
        <Button
          variant="outlined"
          onClick={onBackFromDuplicates}
          disabled={loading}
          color="primary"
        >
          Edit Beer
        </Button>
        {showReuseAction ? (
          <Button
            variant="contained"
            color="primary"
            onClick={onUseExistingBeer}
            disabled={loading}
            startIcon={
              reuseLoading ? <CircularProgress size={20} color="inherit" /> : null
            }
          >
            {reuseLoading ? 'Saving…' : 'Use existing beer'}
          </Button>
        ) : null}
        {showAddAnywayAction ? (
          <Button
            variant="contained"
            color="warning"
            onClick={onAddAnyway}
            disabled={loading}
            startIcon={
              addAnywayLoading ? <CircularProgress size={20} color="inherit" /> : null
            }
          >
            {addAnywayLoading ? 'Saving…' : 'Add anyway'}
          </Button>
        ) : null}
      </>
    );
  }

  return (
    <>
      <Button onClick={onClose} disabled={loading}>
        Cancel
      </Button>
      <Button
        onClick={onSubmit}
        variant="contained"
        color="primary"
        disabled={loading}
        startIcon={
          loading ? <CircularProgress size={20} color="inherit" /> : null
        }
      >
        {loading ? loadingLabel : isEditMode ? 'Save' : 'Add beer'}
      </Button>
    </>
  );
}

AddBeerDialogActionButtons.propTypes = {
  showDuplicateActions: PropTypes.bool.isRequired,
  showReuseAction: PropTypes.bool.isRequired,
  showAddAnywayAction: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  loadingLabel: PropTypes.string.isRequired,
  reuseLoading: PropTypes.bool.isRequired,
  addAnywayLoading: PropTypes.bool.isRequired,
  isEditMode: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onBackFromDuplicates: PropTypes.func.isRequired,
  onUseExistingBeer: PropTypes.func.isRequired,
  onAddAnyway: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

/**
 * AddBeerDialog — add or edit beer via addBeer / updateBeer callables.
 */
function AddBeerDialog({
  open,
  onClose,
  lockedBreweryId,
  lockedBreweryName,
  breweryGroupId = null,
  onSuccess,
  mode = 'add',
  editBeer = null,
  sourceType = 'engage_user',
}) {
  const isEditMode = mode === 'edit';
  const session = useContext(BrewerySessionContext) ?? defaultBrewerySession;
  const { locationFilteredBeers } = useContext(BrewhouseBeerCatalogContext);
  const [authUser] = useAuthState(auth);
  const { addSingleBeer, loading: addLoading, errorMessage: addErrorMessage, clearError: clearAddError } =
    useBeerAddBeer();
  const {
    updateBeerAvailability,
    loading: availabilityLoading,
    errorMessage: availabilityErrorMessage,
    clearError: clearAvailabilityError,
  } = useBeerAvailability();
  const { updateBeer, loading: updateLoading, errorMessage: updateErrorMessage, clearError: clearUpdateError } =
    useBeerUpdate();
  const loading = addLoading || updateLoading || availabilityLoading;
  const [reconcilingPortfolio, setReconcilingPortfolio] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const isBusy = loading || reconcilingPortfolio;
  const errorMessage = addErrorMessage || updateErrorMessage || availabilityErrorMessage;
  const clearError = useCallback(() => {
    clearAddError();
    clearUpdateError();
    clearAvailabilityError();
  }, [clearAddError, clearAvailabilityError, clearUpdateError]);
  const [formData, setFormData] = useState({
    name: '',
    abv: '',
    description: '',
  });
  const [styleType, setStyleType] = useState('');
  const [catalogStyleId, setCatalogStyleId] = useState('');
  const [styleLabel, setStyleLabel] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [selectedBrewery, setSelectedBrewery] = useState(null);
  const [breweryInput, setBreweryInput] = useState('');
  const debouncedBreweryQuery = useDebouncedValue(breweryInput.trim(), 300);
  const { options: breweryOptions, loading: brewerySearchLoading } =
    useBreweryAutocompleteSearch(debouncedBreweryQuery);
  const [duplicateNormalized, setDuplicateNormalized] = useState(null);
  const [pendingDuplicateAction, setPendingDuplicateAction] = useState(null);
  const [serverFieldErrors, setServerFieldErrors] = useState(null);
  const [selectedLocationIds, setSelectedLocationIds] = useState([]);
  const [locationValidationError, setLocationValidationError] = useState('');
  const styleFieldsTouchedRef = useRef(false);
  const locationSelectionInitializedRef = useRef(false);
  const initialLocationIdsRef = useRef([]);
  const dialogFormInitializedRef = useRef(false);
  const catalogBeersRef = useRef(locationFilteredBeers);

  useEffect(() => {
    catalogBeersRef.current = locationFilteredBeers;
  }, [locationFilteredBeers]);

  const breweryLocked = Boolean(lockedBreweryId);
  const resolvedPrimaryBreweryId = breweryLocked
    ? lockedBreweryId
    : selectedBrewery?.id || '';
  const {
    selectableLocations,
    defaultSelectedIds,
    showLocationSelector,
    loading: locationOptionsLoading,
  } = useAddBeerLocationOptions({
    primaryBreweryId: resolvedPrimaryBreweryId,
    breweryGroupId,
    primaryBreweryName: breweryLocked ? lockedBreweryName : selectedBrewery?.name,
    primaryBreweryCity: breweryLocked ? '' : selectedBrewery?.city,
    enabled: open,
  });

  const { beerStyles } = useBeerStyles();
  const styleNameById = useMemo(() => {
    const map = {};
    beerStyles.forEach((styleRow) => {
      map[styleRow.id] = styleRow.name || styleRow.id;
    });
    return map;
  }, [beerStyles]);

  useEffect(() => {
    if (!open) {
      locationSelectionInitializedRef.current = false;
      initialLocationIdsRef.current = [];
      dialogFormInitializedRef.current = false;
      return;
    }
    if (dialogFormInitializedRef.current) {
      // Keep in-progress edits when editBeer updates from the live catalog during save.
      return;
    }
    if (isEditMode && !editBeer) {
      return;
    }
    dialogFormInitializedRef.current = true;
    styleFieldsTouchedRef.current = false;
    const resetState = getOpenDialogFormReset({ isEditMode, editBeer });
    setFormData(resetState.formData);
    setCatalogStyleId(resetState.catalogStyleId);
    setStyleLabel(resetState.styleLabel);
    setStyleType(resetState.styleType);
    setFormErrors({});
    setSelectedBrewery(null);
    setBreweryInput('');
    setDuplicateNormalized(null);
    setPendingDuplicateAction(null);
    setServerFieldErrors(null);
    setSelectedLocationIds([]);
    setLocationValidationError('');
    setWarningMessage('');
    setReconcilingPortfolio(false);
    clearError();
  }, [open, clearError, isEditMode, editBeer]);

  useEffect(() => {
    if (!open || isEditMode || defaultSelectedIds.length === 0) {
      return;
    }
    if (locationSelectionInitializedRef.current) {
      return;
    }
    setSelectedLocationIds(defaultSelectedIds);
    initialLocationIdsRef.current = [...defaultSelectedIds];
    setLocationValidationError('');
    locationSelectionInitializedRef.current = true;
  }, [open, isEditMode, defaultSelectedIds]);

  useEffect(() => {
    if (!open || !isEditMode || !editBeer || locationOptionsLoading) {
      return;
    }
    if (selectableLocations.length <= 1) {
      return;
    }
    if (locationSelectionInitializedRef.current) {
      return;
    }

    const validLocationIds = new Set(selectableLocations.map((location) => location.id));
    const seededIds = getInitialLocationIds(editBeer).filter((breweryId) => (
      validLocationIds.has(breweryId)
    ));
    const fallbackIds = seededIds.length > 0
      ? seededIds
      : selectableLocations.map((location) => location.id);

    setSelectedLocationIds(fallbackIds);
    initialLocationIdsRef.current = [...fallbackIds];
    setLocationValidationError('');
    locationSelectionInitializedRef.current = true;
  }, [
    open,
    isEditMode,
    editBeer,
    locationOptionsLoading,
    selectableLocations,
  ]);

  useEffect(() => {
    if (!open || !isEditMode || !editBeer) {
      return;
    }
    const hydrationFields = getEditStyleHydrationFields({
      editBeer,
      beerStyles,
      styleType,
      catalogStyleId,
      styleLabel,
      styleFieldsTouched: styleFieldsTouchedRef.current,
    });
    if (!hydrationFields) {
      return;
    }
    setStyleType(hydrationFields.styleType);
    setCatalogStyleId(hydrationFields.catalogStyleId);
    setStyleLabel(hydrationFields.styleLabel);
  }, [open, isEditMode, editBeer, beerStyles, styleType, catalogStyleId, styleLabel]);

  const handleFieldChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const handleNameChange = useCallback(
    (event) => {
      handleFieldChange('name', event.target.value);
    },
    [handleFieldChange],
  );

  const handleStyleTypeChange = useCallback((nextType) => {
    styleFieldsTouchedRef.current = true;
    setStyleType(nextType);
    setFormErrors((prev) => {
      if (!prev.styleType && !prev.style) {
        return prev;
      }
      const next = { ...prev };
      delete next.styleType;
      delete next.style;
      return next;
    });
  }, []);

  const handleStyleSelectionChange = useCallback(({ styleType: nextType, catalogStyleId: nextStyleId, styleLabel: nextStyleLabel }) => {
    styleFieldsTouchedRef.current = true;
    if (typeof nextType === 'string' && nextType.trim()) {
      setStyleType(nextType);
    }
    setCatalogStyleId(typeof nextStyleId === 'string' ? nextStyleId : '');
    setStyleLabel(typeof nextStyleLabel === 'string' ? nextStyleLabel : '');
    setFormErrors((prev) => {
      if (!prev.styleType && !prev.style) {
        return prev;
      }
      const next = { ...prev };
      delete next.styleType;
      delete next.style;
      return next;
    });
  }, []);

  const handleAbvChange = useCallback(
    (event) => {
      const value = event.target.value;
      if (value === '' || /^\d*\.?\d*$/.test(value)) {
        handleFieldChange('abv', value);
      }
    },
    [handleFieldChange],
  );

  const handleDescriptionChange = useCallback(
    (event) => {
      handleFieldChange('description', event.target.value);
    },
    [handleFieldChange],
  );

  const handleBreweryAutocompleteChange = useCallback((event, newValue) => {
    setSelectedBrewery(newValue);
  }, []);

  const handleBreweryInputChange = useCallback((event, newInputValue) => {
    setBreweryInput(newInputValue);
  }, []);

  const getBreweryOptionLabel = useCallback(
    (option) => option?.name ?? '',
    [],
  );

  const isBreweryOptionEqualToValue = useCallback((option, value) => {
    if (!option || !value) {
      return option === value;
    }
    return option.id === value.id;
  }, []);

  const renderBreweryInput = useCallback(
    (params) => {
      const {
        InputProps,
        slotProps: paramsSlotProps,
        ...textFieldParams
      } = params;
      return (
        <TextField
          {...textFieldParams}
          label="Brewery"
          required
          error={Boolean(formErrors.brewery)}
          helperText={formErrors.brewery || 'Search by brewery name'}
          slotProps={{
            ...paramsSlotProps,
            input: {
              ...paramsSlotProps?.input,
              ...InputProps,
              endAdornment: (
                <>
                  {brewerySearchLoading ? (
                    <CircularProgress color="inherit" size={18} />
                  ) : null}
                  {InputProps?.endAdornment}
                </>
              ),
            },
          }}
        />
      );
    },
    [brewerySearchLoading, formErrors.brewery],
  );

  const validateForm = useCallback(() => {
    const errors = validateAddBeerForm({
      formData,
      styleType,
      catalogStyleId,
      styleLabel,
      isEditMode,
      breweryLocked,
      selectedBrewery,
    });
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, breweryLocked, selectedBrewery, styleType, catalogStyleId, styleLabel, isEditMode]);

  const resetDuplicateAndServerErrors = useCallback(() => {
    setDuplicateNormalized(null);
    setPendingDuplicateAction(null);
    setServerFieldErrors(null);
  }, []);

  const handleClose = useCallback(() => {
    if (!isBusy) {
      clearError();
      setFormErrors({});
      setWarningMessage('');
      setReconcilingPortfolio(false);
      resetDuplicateAndServerErrors();
      onClose();
    }
  }, [isBusy, clearError, onClose, resetDuplicateAndServerErrors]);

  const finalizeNormalizedResponse = useCallback(
    (normalized, breweryIdValue, options = {}) => {
      applyNormalizedAddResponse({
        normalized,
        breweryIdValue,
        formNameTrim: formData.name.trim(),
        wasForceAdd: options.wasForceAdd === true,
        raw: options.raw ?? null,
        onSuccess,
        handleClose,
        setDuplicateNormalized,
        setServerFieldErrors,
        setFormErrors,
      });
    },
    [formData.name, handleClose, onSuccess],
  );

  const resolveAddBeerFailure = useCallback(
    async (failedResult, breweryIdValue, knownCatalogBeerIds) => {
      setFormErrors({});
      setServerFieldErrors(null);
      setWarningMessage('');
      clearError();

      if (failedResult?.isTransient) {
        setReconcilingPortfolio(true);
        try {
          const catalogMatch = await reconcileAddBeerAfterCallableError({
            catalogBeers: () => catalogBeersRef.current,
            beerName: formData.name.trim(),
            breweryId: breweryIdValue,
            knownCatalogBeerIds,
          });
          if (catalogMatch) {
            try {
              trackAddBeerRecoveredFromError({
                breweryId: breweryIdValue,
                beerId: catalogMatch.id,
              });
            } catch {
              // Analytics must not block save recovery.
            }
            if (onSuccess) {
              onSuccess({
                outcome: 'added',
                beerId: catalogMatch.id,
                beerName: catalogMatch.name || formData.name.trim(),
                breweryId: breweryIdValue,
                recoveredFromError: true,
              });
            }
            handleClose();
            return true;
          }
        } finally {
          setReconcilingPortfolio(false);
        }

        try {
          trackAddBeerTransientUnconfirmed({ breweryId: breweryIdValue });
        } catch {
          // Analytics must not block the dialog.
        }
        setWarningMessage(
          getUserFacingCallableError(failedResult.rawError, {
            operation: 'addBeer',
            afterReconcile: true,
          }),
        );
        return true;
      }

      setFormErrors({
        general: failedResult?.error || 'Failed to add beer',
      });
      return false;
    },
    [clearError, formData.name, handleClose, onSuccess],
  );

  const snapshotCatalogBeerIds = useCallback(() => {
    const beerIds = catalogBeersRef.current
      .map((beerRow) => (typeof beerRow?.id === 'string' ? beerRow.id.trim() : ''))
      .filter(Boolean);
    return new Set(beerIds);
  }, []);

  const prepareSaveContext = useCallback(({ preserveDuplicateState = false } = {}) => {
    if (preserveDuplicateState) {
      setServerFieldErrors(null);
    } else {
      resetDuplicateAndServerErrors();
    }
    if (!validateForm()) {
      return null;
    }

    if (showLocationSelector && selectedLocationIds.length === 0) {
      setLocationValidationError('Select at least one location where this beer should be listed.');
      return null;
    }
    setLocationValidationError('');

    const styleResolution = resolveStyleForSave({ catalogStyleId, styleLabel, styleType });
    if (!styleResolution.ok) {
      setFormErrors(styleResolution.errors);
      return null;
    }

    return {
      resolvedStyle: styleResolution.value,
      abvNum: parseAbvFromForm(formData.abv),
      availableBreweryIds: showLocationSelector ? selectedLocationIds : undefined,
    };
  }, [
    catalogStyleId,
    formData.abv,
    isEditMode,
    resetDuplicateAndServerErrors,
    selectedLocationIds,
    showLocationSelector,
    styleLabel,
    styleType,
    validateForm,
  ]);

  const handleSubmit = useCallback(async () => {
    const saveContext = prepareSaveContext();
    if (!saveContext) {
      return;
    }

    const { resolvedStyle, abvNum, availableBreweryIds } = saveContext;

    if (isEditMode) {
      const breweryId = lockedBreweryId;
      if (!editBeer?.id || !breweryId) {
        setFormErrors({ general: 'Missing beer ID or brewery ID' });
        return;
      }

      const result = await updateBeer({
        beerId: editBeer.id,
        breweryId,
        ...buildSavePayload({ formData, resolvedStyle, abvNum }),
      });

      if (!result.updated) {
        setFormErrors({ general: result.error || updateErrorMessage || 'Failed to update beer' });
        return;
      }

      const locationsChanged = showLocationSelector && haveLocationIdsChanged(
        initialLocationIdsRef.current,
        selectedLocationIds,
      );

      if (locationsChanged) {
        const availabilityResult = await updateBeerAvailability({
          beerId: editBeer.id,
          availableBreweryIds: selectedLocationIds,
          mode: 'replace',
        });

        if (!availabilityResult) {
          setFormErrors({
            general:
              availabilityErrorMessage
              || 'Beer details saved, but location availability failed to update.',
          });
          return;
        }
      }

      if (onSuccess) {
        onSuccess({
          outcome: 'updated',
          beerId: result.beerId || editBeer.id,
          beerName: formData.name.trim(),
          breweryId,
        });
      }
      handleClose();
      return;
    }

    const uid = authUser?.uid;
    if (!uid) {
      setFormErrors({ general: 'You must be signed in to add a beer.' });
      return;
    }
    const breweryId = breweryLocked ? lockedBreweryId : selectedBrewery?.id;
    if (!breweryId) {
      setFormErrors({ general: 'Brewery is required' });
      return;
    }

    if (
      showLocationSelector
      && session.breweryId
      && !selectedLocationIds.includes(session.breweryId)
    ) {
      try {
        trackAddBeerActiveLocationUnchecked({ breweryId: breweryId || session.breweryId });
      } catch {
        // Analytics must not block save.
      }
    }

    const knownCatalogBeerIds = snapshotCatalogBeerIds();

    const result = await addSingleBeer({
      breweryId,
      uid,
      beer: buildSavePayload({ formData, resolvedStyle, abvNum }),
      sourceType,
      availableBreweryIds,
    });

    if (!result.ok) {
      await resolveAddBeerFailure(result, breweryId, knownCatalogBeerIds);
      return;
    }

    finalizeNormalizedResponse(result.normalized, breweryId, {
      raw: result.raw,
    });
  }, [
    addSingleBeer,
    authUser,
    breweryLocked,
    editBeer,
    finalizeNormalizedResponse,
    formData,
    handleClose,
    isEditMode,
    lockedBreweryId,
    onSuccess,
    prepareSaveContext,
    resolveAddBeerFailure,
    selectedBrewery,
    snapshotCatalogBeerIds,
    selectedLocationIds,
    session.breweryId,
    showLocationSelector,
    sourceType,
    updateBeer,
    updateBeerAvailability,
    updateErrorMessage,
    availabilityErrorMessage,
  ]);

  const handleAddAnyway = useCallback(async () => {
    setFormErrors({});
    setServerFieldErrors(null);
    clearError();

    if (!canForceAddDuplicate(duplicateNormalized)) {
      return;
    }

    const saveContext = prepareSaveContext({ preserveDuplicateState: true });
    if (!saveContext) {
      return;
    }

    const uid = authUser?.uid;
    if (!uid) {
      setFormErrors({ general: 'You must be signed in to add a beer.' });
      return;
    }
    const breweryId = breweryLocked ? lockedBreweryId : selectedBrewery?.id;
    if (!breweryId) {
      setFormErrors({ general: 'Brewery is required' });
      return;
    }

    const { resolvedStyle, abvNum, availableBreweryIds } = saveContext;
    try {
      trackAddBeerCreateAnyway({ breweryId });
    } catch {
      // Analytics must not block save.
    }
    setPendingDuplicateAction('add-anyway');
    try {
      const knownCatalogBeerIds = snapshotCatalogBeerIds();
      const result = await addSingleBeer({
        breweryId,
        uid,
        beer: buildSavePayload({ formData, resolvedStyle, abvNum }),
        forceAddDuplicate: true,
        beerDuplicateReviewId: getDuplicateReviewId(duplicateNormalized),
        sourceType,
        availableBreweryIds,
      });

      if (!result.ok) {
        await resolveAddBeerFailure(result, breweryId, knownCatalogBeerIds);
        return;
      }

      finalizeNormalizedResponse(result.normalized, breweryId, {
        raw: result.raw,
        wasForceAdd: true,
      });
    } finally {
      setPendingDuplicateAction(null);
    }
  }, [
    addSingleBeer,
    authUser,
    breweryLocked,
    clearError,
    duplicateNormalized,
    finalizeNormalizedResponse,
    formData,
    lockedBreweryId,
    prepareSaveContext,
    resolveAddBeerFailure,
    selectedBrewery,
    snapshotCatalogBeerIds,
    sourceType,
  ]);

  const handleSelectedLocationIdsChange = useCallback((nextIds) => {
    setSelectedLocationIds(nextIds);
    locationSelectionInitializedRef.current = true;
    if (nextIds.length > 0) {
      setLocationValidationError('');
    }
    try {
      trackAddBeerSelectLocations({
        breweryId: resolvedPrimaryBreweryId,
        selectedCount: nextIds.length,
      });
    } catch {
      // Analytics must not block the dialog.
    }
  }, [resolvedPrimaryBreweryId]);

  const handleUseExistingBeer = useCallback(async () => {
    setFormErrors({});
    setServerFieldErrors(null);
    clearError();

    const matchedBeer = getMatchedBeerForReuse(duplicateNormalized);
    if (!matchedBeer?.id) {
      setFormErrors({ general: 'No existing beer was found to reuse.' });
      return;
    }

    if (!isEditMode && showLocationSelector && selectedLocationIds.length === 0) {
      setLocationValidationError('Select at least one location where this beer should be listed.');
      return;
    }

    const breweryId = breweryLocked ? lockedBreweryId : selectedBrewery?.id;
    setPendingDuplicateAction('reuse');
    try {
      const result = await updateBeerAvailability({
        beerId: matchedBeer.id,
        availableBreweryIds: selectedLocationIds,
        mode: 'merge',
      });

      if (!result) {
        setFormErrors({
          general: availabilityErrorMessage || 'Failed to add beer to selected locations.',
        });
        return;
      }

      try {
        trackAddBeerReuseExisting({
          breweryId: breweryId || matchedBeer.breweryId || '',
          beerId: matchedBeer.id,
        });
      } catch {
        // Analytics must not block save.
      }
      if (onSuccess) {
        onSuccess({
          outcome: 'reused',
          beerId: matchedBeer.id,
          beerName: matchedBeer.name || formData.name.trim(),
          breweryId: breweryId || matchedBeer.breweryId || '',
        });
      }
      handleClose();
    } finally {
      setPendingDuplicateAction(null);
    }
  }, [
    availabilityErrorMessage,
    breweryLocked,
    clearError,
    duplicateNormalized,
    formData.name,
    handleClose,
    isEditMode,
    lockedBreweryId,
    onSuccess,
    selectedBrewery,
    selectedLocationIds,
    showLocationSelector,
    updateBeerAvailability,
  ]);

  const handleBackFromDuplicates = useCallback(() => {
    setDuplicateNormalized(null);
    setPendingDuplicateAction(null);
  }, []);

  const showDuplicateResults = Boolean(duplicateNormalized && !isEditMode);
  const showReuseAction = showDuplicateResults && isGroupDuplicateOffer(duplicateNormalized);
  const showAddAnywayAction = showDuplicateResults
    && !showReuseAction
    && canForceAddDuplicate(duplicateNormalized);
  const reuseLoading = pendingDuplicateAction === 'reuse';
  const addAnywayLoading = pendingDuplicateAction === 'add-anyway';

  useEffect(() => {
    if (!showReuseAction || !resolvedPrimaryBreweryId) {
      return;
    }
    const matchedBeer = getMatchedBeerForReuse(duplicateNormalized);
    try {
      trackAddBeerOfferReuse({
        breweryId: resolvedPrimaryBreweryId,
        matchedBeerId: matchedBeer?.id || '',
      });
    } catch {
      // Analytics must not block the dialog.
    }
  }, [duplicateNormalized, resolvedPrimaryBreweryId, showReuseAction]);

  const dialogTitle = getDialogTitle({ duplicateNormalized, isEditMode });
  const submitLoadingLabel = reconcilingPortfolio
    ? ADD_BEER_RECONCILE_LABEL
    : addLoading && !isEditMode
      ? ADD_BEER_LOADING_LABEL
      : 'Saving…';
  const addStatusMessage = !isEditMode && addLoading && !reconcilingPortfolio
    ? ADD_BEER_LOADING_LABEL
    : reconcilingPortfolio
      ? ADD_BEER_RECONCILE_LABEL
      : '';
  const pendingBeer = useMemo(
    () => ({
      name: formData.name.trim(),
      styleLabel: styleLabel || catalogStyleId || '—',
      abv: formData.abv,
      description: formData.description,
    }),
    [formData.name, formData.abv, formData.description, styleLabel, catalogStyleId],
  );

  if (isEditMode && !editBeer) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth={showDuplicateResults ? 'md' : 'sm'}
    >
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        <AddBeerDialogAlerts
          errorMessage={errorMessage}
          formErrors={formErrors}
          serverFieldErrors={serverFieldErrors}
          warningMessage={warningMessage}
          statusMessage={addStatusMessage}
        />
        {showDuplicateResults ? (
          <DuplicateAddBeerResults
            pendingBeer={pendingBeer}
            duplicatedBeers={duplicateNormalized.duplicatedBeers}
            styleNameById={styleNameById}
            sourceType={sourceType}
          />
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {showLocationSelector ? (
              <BeerLocationAvailabilitySelector
                locations={selectableLocations}
                selectedIds={selectedLocationIds}
                onSelectedIdsChange={handleSelectedLocationIdsChange}
                loading={locationOptionsLoading}
                disabled={isBusy}
                validationError={locationValidationError}
              />
            ) : null}
            {isEditMode && !showLocationSelector ? (
              <Alert severity="info">
                Name, style, ABV, and description apply wherever this beer is listed.
              </Alert>
            ) : null}
            <AddBeerDialogFormFields
            breweryLocked={breweryLocked}
            isEditMode={isEditMode}
            breweryOptions={breweryOptions}
            brewerySearchLoading={brewerySearchLoading}
            selectedBrewery={selectedBrewery}
            breweryInput={breweryInput}
            formData={formData}
            formErrors={formErrors}
            styleType={styleType}
            catalogStyleId={catalogStyleId}
            styleLabel={styleLabel}
            loading={isBusy}
            onBreweryAutocompleteChange={handleBreweryAutocompleteChange}
            onBreweryInputChange={handleBreweryInputChange}
            getBreweryOptionLabel={getBreweryOptionLabel}
            isBreweryOptionEqualToValue={isBreweryOptionEqualToValue}
            renderBreweryInput={renderBreweryInput}
            onNameChange={handleNameChange}
            onStyleTypeChange={handleStyleTypeChange}
            onStyleSelectionChange={handleStyleSelectionChange}
            onAbvChange={handleAbvChange}
            onDescriptionChange={handleDescriptionChange}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <AddBeerDialogActionButtons
          showDuplicateActions={showDuplicateResults}
          showReuseAction={showReuseAction}
          showAddAnywayAction={showAddAnywayAction}
          loading={isBusy}
          loadingLabel={submitLoadingLabel}
          reuseLoading={reuseLoading}
          addAnywayLoading={addAnywayLoading}
          isEditMode={isEditMode}
          onClose={handleClose}
          onBackFromDuplicates={handleBackFromDuplicates}
          onUseExistingBeer={handleUseExistingBeer}
          onAddAnyway={handleAddAnyway}
          onSubmit={handleSubmit}
        />
      </DialogActions>
    </Dialog>
  );
}

AddBeerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  lockedBreweryId: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  lockedBreweryName: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  breweryGroupId: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  onSuccess: PropTypes.func,
  mode: PropTypes.oneOf(['add', 'edit']),
  editBeer: PropTypes.shape({
    id: PropTypes.string.isRequired,
    breweryId: PropTypes.string,
    name: PropTypes.string,
    style: PropTypes.string,
    abv: PropTypes.number,
    description: PropTypes.string,
    availableBreweryIds: PropTypes.arrayOf(PropTypes.string),
  }),
  sourceType: PropTypes.string,
};

AddBeerDialog.defaultProps = {
  lockedBreweryId: null,
  lockedBreweryName: null,
  breweryGroupId: null,
  onSuccess: null,
  mode: 'add',
  editBeer: null,
  sourceType: 'engage_user',
};

export default AddBeerDialog;
